<template>
  <div class="choropleth">
    <div class="mapWrapper">
      <SvgPanZoom 
        style="width: 100%; height: 100%;"
        :zoomEnabled="true"
        :panEnabled="true" 
        :controlIconsEnabled="false"
        :mouseWheelZoomEnabled="false"
        :preventMouseEventsDefault="false"
        :dblClickZoomEnabled="false"
        :fit="false"
        :center="false"
        :minZoom="1"
        :onZoom="zoomChanged"
        :onUpdatedCTM="updatedCTM"
        @svgpanzoom="registerSvgPanZoom"
      >
        <svg width="100%" :height="height" ref="svg">
          <rect x="0" y="0" :width="width" :height="height" fill="transparent" @click.self="resetRegion()"></rect>
          <pattern id="nodata" width="6" height="6" patternUnits="userSpaceOnUse">
            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <polygon fill="#ddd" points="0 0 3 0 6 3 6 6"></polygon>
              <polygon fill="#ddd" points="0 3 3 6 0 6"></polygon>
            </g>
          </pattern>
          <g class="map">
            <path v-for="(polygon, index) in polygons" 
              v-bind:key="'polygon'+index"
              class="polygon" 
              :d="polygon.path" 
              :fill="getFill(polygon)" 
              @mouseover="mouseover(polygon)" 
              @mouseleave="mouseleave()"
              @click.self="selectRegion(polygon)"
              :style="{strokeWidth: strokeWidthComputed}"
            >
            </path>
            <!-- <path v-for="polygon in polygons" class="polygon" :d="polygon.path" fill="url(#nodata)" @mouseover="mouseover(polygon)" @mouseleave="mouseleave()"></path> -->
            
          </g>
          <g class="map">
            <path v-for="(polygon, index) in polygonsSupraregional" 
              v-bind:key="'region'+index"
              class="polygon bl" 
              :d="polygon.path"
              :style="{strokeWidth: strokeWidthComputed}"
            >
            </path>
            <path v-if="value" class="polygon hover" :d="highlightPath"></path>
            <path v-if="hover" class="polygon hover" :d="hover.path"></path>
          </g>
          <g class="map">
            <g v-for="marker in markers" :key="marker.label">
              <text 
                :y="project([marker.lat, marker.long])[1]-strokeWidthComputed*7" 
                :x="project([marker.lat, marker.long])[0]" 
                :text-anchor="marker.anchor||'middle'"
                class="marker shaddow"
                :stroke-width="strokeWidthComputed*2"
                :font-size="strokeWidthComputed*14"
              >
                {{marker.label}}
              </text>
              <text 
                :y="project([marker.lat, marker.long])[1]-strokeWidthComputed*7" 
                :x="project([marker.lat, marker.long])[0]" 
                :text-anchor="marker.anchor||'middle'"
                class="marker"
                :font-size="strokeWidthComputed*14"
              >
                {{marker.label}}
              </text>
              <circle 
                class="marker-dot"
                :cx="project([marker.lat, marker.long])[0]" 
                :cy="project([marker.lat, marker.long])[1]" 
                :r="strokeWidthComputed*3"  
                fill="#fff" 
                stroke="rgba(17,17,17,1)" 
                :stroke-width="strokeWidthComputed" 
              />
            </g>
          </g>
        </svg>
      </SvgPanZoom>
      <div class="zoomControls">
        <div class="button plus" @click="zoomIn">+</div>
        <div class="button minus" @click="zoomOut">−</div>
        <div class="button reset" v-show="zoomLevel > 1.01" @click="resetZoom"></div>
      </div>
      <!-- <div class="resetButton" v-show="zoomLevel > 1.01" @click="resetZoom">Übersicht</div> -->
    </div>
    <div class="tooltip" v-if="hover" :style="{'left': hover.x + 'px', 'top': hover.y-20 + 'px'}">
      <b>{{hover.data.regionName}}</b><br>
      <div v-if="hover.data.tooltip.length==0">
        noch nicht ausgezählt
      </div>
      <div v-for="(p, index) in hover.data.tooltip" v-bind:key="index">
        {{p}}
      </div>
    </div>
    <div class="legend" v-if="settings.legend">
      <div class="categories" v-if="settings.type == 'category'">
        <div v-for="category in settings.colors" :key="category.id" class="category">
          <span :style="{backgroundColor: category.color}"></span>
          {{category.label}} 
        </div>
      </div>
      <div class="heatmap" v-if="settings.type == 'heatmap'">
        <div class="title">{{settings.title}}</div>
        <div style="position: relative">
          <div class="label-left">{{computedDomain[0]}}<span v-if="settings.suffix">{{settings.suffix}}</span></div>
          <div class="label-right">{{computedDomain[1]}}<span v-if="settings.suffix">{{settings.suffix}}</span></div>
          <div class="gradient" :style="cssGradient(settings.range)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import debounce from 'debounce'
import SvgPanZoom from 'vue-svg-pan-zoom'

d3.formatDefaultLocale({
  'decimal': ',',
  'thousands': '.',
  'grouping': [3],
  'currency': ['', '€'],
  'dateTime': '%a %b %e %X %Y',
  'date': '%d.%m.%Y',
  'time': '%H:%M:%S',
  'periods': ['AM', 'PM'],
  'days': ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  'shortDays': ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  'months': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  'shortMonths': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
})

export default {
  name: 'Map',
  components: {
    SvgPanZoom
  },
  props: {
    data: Array,
    settings: Object,
    geoJson: Object,
    geoJsonSupraregional: Object,
    markers: Array,
    domain: {
      type: Array,
      default () {
        return [0, 20]
      }
    },
    range: {
      type: Array,
      default () {
        return ['#fff', '#111']
      }
    },
    selectedRegion: Object,
    view: String,
    maxHeight: {
      type: Number,
      default: 600
    },
    value: Object,
    showAverage: {
      type: Boolean,
      default: false
    },
    year: String
  },
  data () {
    return {
      projection: d3.geoMercator(),
      polygons: [],
      polygonsSupraregional: [],
      height: this.maxHeight,
      width: 600,
      hover: null,
      zoomLevel: 1.0,
      strokeWidth: 1.0,
      panEnabled: false
    }
  },
  methods: {
    updatedCTM () {
      if (this.zoomLevel < 1.000001) {
        this.svgpanzoom.reset()
      }
    },
    registerSvgPanZoom (svgpanzoom) {
        this.svgpanzoom = svgpanzoom
        this.svgpanzoom.disablePan()
    },
    zoomIn () {
        if( !this.svgpanzoom ) return
        this.svgpanzoom.zoomIn()
    },
    zoomOut () {
        if( !this.svgpanzoom ) return
        this.svgpanzoom.zoomOut()
    },
    resetZoom () {
        if( !this.svgpanzoom ) return
        this.svgpanzoom.reset()
    },
    zoomChanged (zoom) {
      this.zoomLevel = zoom
      if (zoom < 1.000001) {
        this.svgpanzoom.disablePan()

      } else {
        this.svgpanzoom.enablePan()
      }
    },
    fitProjection () {
      this.width = this.$refs.svg.getBoundingClientRect().width
      let height = this.maxHeight

      this.projection.scale(1).translate([0, 0])
      var b = this.path.bounds(this.geoJson)
      var s = 1 / Math.max((b[1][0] - b[0][0]) / this.width, (b[1][1] - b[0][1]) / height)

      this.height = (b[1][1] - b[0][1]) * s

      var t = [(this.width - s * (b[1][0] + b[0][0])) / 2, (this.height - s * (b[1][1] + b[0][1])) / 2]

      this.projection
        .scale(s)
        .translate(t)

      this.polygons = this.geoJson.features.map(f => {
        return {
          path: this.path(f),
          feature: f,
          data: this.data.find( d => d.regionKey == f.properties[this.settings.regionKey] )
        }
      })

      let filter2016 = ["06437016","06633030"]
      let filter2021 = ["06437008","06437014","06437002","06437015","06633027","06633021"]

      if (this.year == "2016") {
        this.polygons = this.polygons.filter( p => filter2016.indexOf(p.feature.properties.AGS) < 0 )
      } else {
        this.polygons = this.polygons.filter( p => filter2021.indexOf(p.feature.properties.AGS) < 0 )
      }

      this.polygonsSupraregional = this.geoJsonSupraregional.features.map(f => {
        return {
          path: this.path(f)
        }
      })
    },
    project (array) {
      return this.projection(array)
    },
    getColor( value ) {
      return this.colorScale(value*12)
    },
    getFill (polygon) {
      if (!polygon.data) {
        return 'url(#nodata)'
      }
      if (this.settings.type == 'heatmap') {
        return this.colorScale(polygon.data.value)||'url(#nodata)'
      }
      if (this.settings.type == 'category') {
        if(polygon.data.category == 'nodata') {
          return 'url(#nodata)'
        }
        let match = this.settings.colors.find(party => party.id == polygon.data.category)
        return match ? match.color : '#efefef'
        // return this.settings.colors.find(party => party.label == polygon.data.category).color||'#efefef'
      }
    },
    mouseover (polygon) {
      let centroid = this.path.centroid(polygon.feature)

      this.hover = {
        data: polygon.data,
        x: this.clamp(Math.round(centroid[0]), 70, this.width-70),
        y: Math.round(centroid[1]),
        path: this.path(polygon.feature)
      }
    },
    clamp (num, min, max) {
      return num <= min ? min : num >= max ? max : num;
    },
    mouseleave () {
      this.hover = null
    },
    selectRegion (polygon) {
      this.$emit('input', { 
        GKZ: polygon.data.regionKey.slice(2),
        Gemeinde: polygon.data.regionName,
        Wahlbeteiligung: polygon.data.Wahlbeteiligung,
        Auszaehlungsstand: polygon.data.Auszaehlungsstand
      })
    },
    resetRegion () {
      this.$emit('input', null)
    },
    cssGradient (colors) {
      return `background: linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%);`
    }
  },
  updated () {
  },
  mounted () {
    this.fitProjection()
    window.addEventListener('resize', debounce(this.fitProjection, 250))

    // this.data.forEach(row => {
    //   let match = this.geoJson.features.find( feature => feature.properties.AGS == row.regionKey)
    //   if(!match) {
    //     console.log(row)
    //   }
    // })
  },
  watch: {
    data: function () {
      this.fitProjection()
    },
    // zoomLevel: function () {
    //   if (this.zoomLevel<1.1) {
    //     this.panEnabled = false
    //   } else {
    //     this.panEnabled =  true
    //     this.registerSvgPanZoom()
    //   }
    // }
  },
  computed: {
    strokeWidthComputed () {
      return Math.round((parseFloat(this.strokeWidth) / this.zoomLevel) * 10) / 10
      // return Math.max((Math.round((this.strokeWidth / this.zoomLevel) * 10) / 10), 0.5)
    },
    computedDomain () {
      if (this.settings.domain) { return this.settings.domain}
      else {
        let max = this.data.reduce( (a,b) => { return b.value > a ? b.value : a }, 0)
        return [0, Math.ceil(max)]
      }
    },
    path () {
      return d3.geoPath().projection(this.projection)
    },
    colorScale () {
      return d3.scaleLinear().domain(this.computedDomain).range(this.settings.range)
      // return d3.scaleLinear().domain(this.category.domain || this.domain).range(this.category.colorRange || this.range)
    },
    highlightPath () {
      return this.polygons.find(p => p.feature.properties[this.settings.regionKey].slice(2) === this.value.GKZ).path
    },
    gradient () {
      let steps = 11
      let firstStep = this.domain[0]
      let lastStep = this.domain.slice(-1)[0]
      let stepSize = (lastStep - firstStep) / steps

      return '.'.repeat(steps).split('').map((d, i) => {
        let value = firstStep + (stepSize * i)
        // value = Math.pow(value, 2)
        return {
          fill: this.colorScale(value*3),
          x: 16 + (i / steps) * (300 - 32),
          width: (300 - 32) / steps
        }
      })
    },
    legendLabels () {
      if(this.view != 'cumulated') {
        return [{
          translate: 'translate(16, 0)',
          text: '0'
        }, {
          translate: `translate(${300 - 16}, 0)`,
          text: 50
        }]
      } else {
        return [{
          translate: 'translate(16, 0)',
          text: '0'
        }, {
          translate: `translate(${300 - 16}, 0)`,
          text: this.domain[2]*3
        }]
      }
    }
  }
}
</script>

<style scoped lang='scss'>
.choropleth {
  box-sizing: border-box;
  position: relative;


  .mapWrapper {
    position: relative;
  }

  .zoomControls {
    position: absolute;
    top: 0;
    left: 0;

    .button {
      width: 35px; height: 35px;
      background: #efefef;
      text-align: center;
      border-radius: 2px;
      margin-bottom: 4px;
      font-size: 20px;
      padding: 4px;
      box-sizing: border-box;
      cursor: pointer;

      &.reset {
        background-image: url("~@/assets/icons/icon-1.svg");
        background-repeat: no-repeat;
        background-size: 16px;
        background-position: 50%;
      }
    }
  }


  .resetButton {
    position: absolute;
    right: 0;
    bottom: 0;
    background: #b2b2b2;
    color: #fff;
    font-family: 'FAZ Gold', Helvetica, Arial, sans-serif;
    font-weight: bold;
    padding: 3px 5px 1px;
    border-radius: 3px;
    cursor: pointer;
  }

  .legend {
    margin-bottom: 20px;

    .categories {
      text-align: center;
      padding: 20px 40px;

      .category {
        display: inline-block;
        line-height: 22px;
        margin-right: 12px;
        margin-bottom: 10px;

        span {
          display: inline-block;
          vertical-align: top;
          width: 20px;
          height: 20px;
          border-radius: 50%;
        }
      }
    }

    .heatmap {
      padding: 10px 40px 20px ;
      height: 60px;

      .title {
        text-align: center;
      }

      .label-left, .label-right {
        position: absolute;
        top:0; 
        font-size: 14px;
      }
      .label-left {
        left: 0;
      }
      .label-right {
        right: 0;
      }

      .gradient {
        height: 16px;
        position: absolute;
        width: 100%;
        top: 20px;
        border-left: 1px solid #ddd;
      }
    }
  }


  .map {
    .polygon {
      stroke: rgba(255,255,255,.5);
      stroke-width: 0.5;
      
      &:hover {
        cursor: pointer;
      }

      &.bl {
        stroke: #fff;
        stroke-width: 1;
        fill: transparent;
        pointer-events: none;
        stroke-linejoin: round;
      }

      &.hover {
        stroke-width: 1.5;
        stroke: #000;
        stroke-linejoin: round;
        fill: none;
        
        &:hover {
          cursor: pointer;
        }
      }
    }

    .marker-dot {
      pointer-events: none;

      // @media screen and (max-width: 600px) {
      //   display: none;
      // }
    }

    .marker {
      pointer-events: none;
      // font-weight: bold;
      font-family: 'FAZ Gold', Helvetica, Arial, sans-serif;
      // text-anchor: middle;
      font-size: 14px;
      fill: #fff;

      // @media screen and (max-width: 600px) {
      //   display: none;
      //   font-size: 11px;
      //   font-weight: normal;
      // }

      &.shaddow {
        fill: rgba(17,17,17,1);
        stroke: rgba(17,17,17,1);
        // stroke-width: 2;
      }
    }
  }

  .tooltip {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: 4px 10px;
    min-width: 100px;
    position: absolute;
    z-index: 100;
    color: #fff;
    font-size: 13px;
    line-height: 1.5;
    background: #333;
    border-radius: 4px;
    transform: translate(-50%, -100%);
    pointer-events: none;
    -webkit-box-shadow: 0px 10px 20px 0px rgba(0,0,0,0.15);
    -moz-box-shadow: 0px 10px 20px 0px rgba(0,0,0,0.15);
    box-shadow: 0px 10px 20px 0px rgba(0,0,0,0.15);

    em {
      font-weight: bold;
    }

    &::after{
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 100%;
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #333;
      clear: both;
    }

    &_headline{
      font-weight: bold;
      padding-bottom: 4px;
      margin-bottom: 0;
      display: block;
    }

    ul{
      list-style: none;
      padding: 0;
      margin: 0;
    }
  }
}
</style>
<style>
  #svg-pan-zoom-reset-pan-zoom {
    display: none;
  }
  #svg-pan-zoom-zoom-out {
    transform: translate(30px,36px) scale(.015);
  }
</style>
